/**
 * Msg 577 · Client-side deploy-detection + auto-reload.
 *
 * On boot, remember the backend's `/api/health/version`. Poll every
 * 5 minutes; if the version changes, show a toast and force a hard
 * reload so the browser fetches the fresh `index.html` + hashed bundle.
 *
 * The frontend also bakes its own build version via
 * `REACT_APP_BUILD_VERSION` — mismatches between the running JS and
 * the backend also trigger a reload. Silent no-op on network errors.
 */
import { toast } from "sonner";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const CLIENT_BUILD = process.env.REACT_APP_BUILD_VERSION || "";

let bootVersion = null;
let started = false;
// Msg 578 · live-version subscribers — the visible footer badge in
// AtlasLayout (and anyone else) can subscribe to be pushed the current
// backend version once the boot fetch resolves.
const listeners = new Set();
export function getLiveVersion() { return bootVersion; }
export function subscribeVersion(cb) {
  listeners.add(cb);
  if (bootVersion) { try { cb(bootVersion); } catch { /* noop */ } }
  return () => listeners.delete(cb);
}
function emit(v) {
  for (const cb of listeners) { try { cb(v); } catch { /* noop */ } }
}

async function fetchVersion() {
  try {
    const r = await fetch(`${BACKEND_URL}/api/health/version`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!r.ok) return null;
    const j = await r.json();
    return j?.version || null;
  } catch {
    return null;
  }
}

function reload(reason) {
  try {
    toast("New version available — refreshing…");
  } catch { /* toast may not be mounted yet */ }
  // Give the toast a beat to render, then force-reload from server.
  setTimeout(() => {
    // `location.reload(true)` is deprecated but still honoured on most
    // browsers as a hard reload hint; append a cache-busting query
    // param on the pathname to be safe.
    try { window.location.reload(true); }
    catch { window.location.href = window.location.pathname + `?_v=${Date.now()}`; }
    void reason;
  }, 400);
}

export function startVersionCheck() {
  if (started) return;
  started = true;

  fetchVersion().then((v) => {
    if (!v) return;
    bootVersion = v;
    emit(v);
    // If the freshly-fetched backend version already disagrees with
    // the build baked into this JS bundle, we're running stale JS —
    // reload immediately.
    if (CLIENT_BUILD && CLIENT_BUILD !== v) {
      reload("client-vs-backend-mismatch-at-boot");
    }
  });

  setInterval(async () => {
    const v = await fetchVersion();
    if (!v || !bootVersion) return;
    if (v !== bootVersion) { emit(v); reload("backend-version-changed"); }
  }, CHECK_INTERVAL_MS);
}
